import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetCityQuery, useCreateCityMutation, useUpdateCityMutation, useGetCountryQuery } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import { showSuccess, showError } from '../../../utils/toast';

const AdminCityForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const presetCountryId = searchParams.get('country_id') || '';
  
  const { data, isLoading } = useGetCityQuery(id!, { skip: !isEdit });
  const [createCity, { isLoading: isCreating }] = useCreateCityMutation();
  const [updateCity, { isLoading: isUpdating }] = useUpdateCityMutation();
  const numericPresetCountryId = presetCountryId ? parseInt(presetCountryId, 10) : null;
  const { data: countryData, isLoading: isCountryLoading } = useGetCountryQuery(numericPresetCountryId as number, {
    skip: !numericPresetCountryId,
  });
  const countryName = countryData?.data?.name;
  const countryNameAr = countryData?.data?.name_ar;

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    country_id: '',
  });

  useEffect(() => {
    if (isEdit && data?.data) {
      const city = data.data;
      setFormData({
        name: city.name || '',
        name_ar: city.name_ar || '',
        country_id: city.country_id ? String(city.country_id) : '',
      });
    } else if (presetCountryId) {
      setFormData(prev => ({ ...prev, country_id: presetCountryId }));
    }
  }, [isEdit, data, presetCountryId]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = formData.name.trim();
    const trimmedNameAr = formData.name_ar.trim();
    const countryId = (presetCountryId || formData.country_id).trim();
    
    if (!trimmedName || trimmedName.length < 3) {
      showError('Name is required and must be at least 3 characters long.');
      return;
    }
    
    if (!trimmedNameAr || trimmedNameAr.length < 3) {
      showError('Name Arabic is required and must be at least 3 characters long.');
      return;
    }
    
    try {
      const submitData = {
        name: trimmedName,
        name_ar: trimmedNameAr,
        country_id: countryId ? parseInt(countryId) : undefined,
      };

      if (isEdit) {
        const result = await updateCity({ id: parseInt(id!), data: submitData }).unwrap();
        showSuccess(result.message || 'City updated successfully!');
      } else {
        const result = await createCity(submitData).unwrap();
        showSuccess(result.message || 'City created successfully!');
      }
      
      navigate(presetCountryId ? `/admin/cities?country_id=${presetCountryId}` : '/admin/cities');
    } catch (err: any) {
      if (err?.data?.errors) {
        const errorMessages = Object.values(err.data.errors).flat().join(', ');
        showError(errorMessages || err?.data?.message || 'Validation failed');
      } else {
        showError(err?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} city. Please try again.`);
      }
    }
  };

  if (isLoading || isCountryLoading) {
    return <PageLoader />;
  }

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                {isEdit ? 'Edit City' : 'Add New City'}
              </h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/dashboard'); }} className="text-muted text-hover-primary">Dashboard</a>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">
                  <a href="#" onClick={(e) => { e.preventDefault(); navigate('/admin/cities'); }} className="text-muted text-hover-primary">Cities</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body pt-0">
                <form onSubmit={handleSubmit} id="kt_modal_new_target_form" className="form fv-plugins-bootstrap5 fv-plugins-framework">
                  <div className="row g-9 my-3">
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Name</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter City Name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6 fv-row fv-plugins-icon-container">
                      <label className="required fs-6 fw-semibold mb-2">Name Arabic</label>
                      <input
                        type="text"
                        className="form-control form-control-solid"
                        placeholder="Enter City Name in Arabic"
                        name="name_ar"
                        value={formData.name_ar}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    
                    {presetCountryId && (
                      <div className="col-md-6 fv-row fv-plugins-icon-container">
                        <label className="fs-6 fw-semibold mb-2">Country</label>
                        <input
                          type="text"
                          className="form-control form-control-solid"
                          value={
                            countryName
                              ? `${countryName}${countryNameAr ? ` (${countryNameAr})` : ''}`
                              : presetCountryId
                          }
                          readOnly
                        />
                      </div>
                    )}
                  </div>
                  
                  <div className="text-center">
                    <button type="button" id="kt_modal_new_target_cancel" className="btn btn-light me-3" onClick={() => navigate('/admin/cities')}>
                      Discard
                    </button>
                    <button type="submit" id="kt_modal_new_target_submit" className="btn btn-base" disabled={isCreating || isUpdating}>
                      <span className="indicator-label">Submit</span>
                      {(isCreating || isUpdating) && (
                        <span className="indicator-progress">
                          Please wait...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCityForm;
